/** PROFILUL EXISTENT AL STRĂZII pe fiecare front stradal al lotului.
 *
 *  Planșa PUD trebuie să arate profilul transversal al străzilor adiacente
 *  (OMAI 1022, 3.3), iar nicio bază de date nu-l are: importerul îl citește
 *  cu un model din ortofotografie și din vederile din stradă
 *  (`importer/dosar/profil_strada.py`), la cererea exporterului, când se
 *  generează planșa — până la primul export cardul spune «necitit încă».
 *  E o aproximare, și ecranul o tratează
 *  așa: arată siguranța modelului și ce n-a văzut bine, iar omul poate
 *  rescrie benzile, ca pe orice altă valoare citită din acte. Corecția intră
 *  în `manual.profile_strada.<latura>` cu sursa `manual`; «revino» o șterge
 *  și readuce citirea.
 *
 *  Benzile se editează ca text, un rând pe bandă, «fel lățime»: e cum le-ar
 *  dicta un arhitect («trotuar 2; carosabil 7 sens unic; verde 3»), și intră
 *  într-un câmp mic în loc de o listă de șapte selectoare. Sensul e pe banda
 *  de carosabil («sens unic» / «dublu sens» după lățime), nu pe stradă. */
import { useEffect, useState } from "react";
import { FELURI_BANDA, type Front, type Localizare, type ProfilStrada } from "./dosarclient";
import { numar, useT } from "../i18n";
import type { Cheie } from "../i18n";

/** Benzile ca text, un rând pe bandă: «carosabil 7,00 sens unic». */
export function benziCaText(p: ProfilStrada | null | undefined): string {
  return (p?.benzi ?? [])
    .map((b) => `${b.fel.replace(/_/g, " ")} ${b.latime_m.toFixed(2).replace(".", ",")}`
                + (b.sens === "unic" ? " sens unic" : b.sens === "dublu" ? " dublu sens" : ""))
    .join("\n");
}

/** Textul tastat de om înapoi în benzi. Felul se potrivește fără diacritice,
 *  cu spații sau cu `_` («parcare paralela» = `parcare_paralela`); lățimea
 *  acceptă virgula; după ea, pe carosabil, «sens unic» / «dublu sens» (sau
 *  doar «unic» / «dublu»). Un rând de neînțeles oprește tot: întoarce
 *  eroarea și ecranul spune care rând. */
export function benziDinText(text: string):
    { benzi: ProfilStrada["benzi"] } | { eroare: string } {
  const benzi: ProfilStrada["benzi"] = [];
  const norm = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[\s_-]+/g, "_").replace(/^_|_$/g, "");
  for (const rand of text.split(/[\n;]+/).map((r) => r.trim()).filter(Boolean)) {
    const m = /^(.*?)[\s:]+(\d+(?:[.,]\d+)?)\s*(?:m\b)?\s*(.*)$/.exec(rand);
    const fel = m ? FELURI_BANDA.find((f) => f === norm(m[1])) : undefined;
    const w = m ? parseFloat(m[2].replace(",", ".")) : NaN;
    const coada = m ? norm(m[3]) : "";
    const sens = coada === "" ? null : /^(sens_)?unic$/.test(coada) ? "unic"
      : /^(sens_)?dublu(_sens)?$/.test(coada) ? "dublu" : undefined;
    if (!fel || !(w > 0) || sens === undefined || (sens && fel !== "carosabil")) return { eroare: rand };
    benzi.push({ fel, latime_m: Math.round(w * 20) / 20, ...(fel === "carosabil" ? { sens } : {}) });
  }
  return { benzi };
}

const CULOARE_SIGURANTA = (s: number | null) =>
  s == null ? "chip" : s >= 0.7 ? "chip c-ok" : s >= 0.5 ? "chip c-amb" : "chip c-err";

export function ProfilStradaCard({ loc, potScrie, salveaza }: {
  loc: Localizare;
  potScrie: boolean;
  salveaza: (corectii: Record<string, unknown>) => Promise<boolean>;
}) {
  const t = useT();
  const fronturi: Front[] = loc.fronturi ?? [];
  const profile = loc.profile_strada ?? {};
  // O stradă pe două laturi (colț, front frânt) are același profil: se arată o
  // dată, pe prima latură, ca pe planșă.
  const vazute = new Set<string>();
  const randuri = fronturi.filter((f) => {
    const nume = f.catre ?? `#${f.latura}`;
    if (vazute.has(nume)) return false;
    vazute.add(nume);
    return true;
  });
  if (!randuri.length) return null;
  return (
    <div className="card" style={{ padding: "14px 18px", display: "flex",
                                   flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <b>{t("loc.profil_strada")}</b>
        <span style={{ color: "var(--mut)", fontSize: 12 }}>{t("loc.profil_strada_sub")}</span>
      </div>
      {randuri.map((f) => (
        <ProfilFront key={f.latura} front={f} profil={profile[String(f.latura)] ?? null}
                     atins={!!loc.manual?.includes(`profile_strada.${f.latura}`)}
                     potScrie={potScrie} salveaza={salveaza} />
      ))}
    </div>
  );
}

function ProfilFront({ front, profil, atins, potScrie, salveaza }: {
  front: Front;
  profil: ProfilStrada | null;
  atins: boolean;
  potScrie: boolean;
  salveaza: (corectii: Record<string, unknown>) => Promise<boolean>;
}) {
  const t = useT();
  const [editez, setEditez] = useState(false);
  const [text, setText] = useState(benziCaText(profil));
  const [eroare, setEroare] = useState("");
  const [ocupat, setOcupat] = useState(false);
  // Citirea venită între timp (o rederivare) înlocuiește ciorna doar cât nu
  // se editează: altfel i-ar lua omului textul de sub mână.
  useEffect(() => { if (!editez) setText(benziCaText(profil)); }, [profil, editez]);

  const cheie = `profile_strada`;
  const scrie = async () => {
    const r = benziDinText(text);
    if ("eroare" in r) { setEroare(t("loc.profil_rand_neinteles", { rand: r.eroare })); return; }
    setOcupat(true);
    const nou: ProfilStrada = {
      catre: front.catre, benzi: r.benzi,
      latime_totala_m: Math.round(r.benzi.reduce((s, b) => s + b.latime_m, 0) * 10) / 10,
      capat_opus: profil?.capat_opus ?? null,
      siguranta: null, observatii: null, sursa: "manual",
    };
    const ok = await salveaza({ [cheie]: { [String(front.latura)]: nou } });
    setOcupat(false);
    if (ok) { setEroare(""); setEditez(false); }
  };
  const revino = async () => {
    setOcupat(true);
    await salveaza({ [cheie]: { [String(front.latura)]: null } });
    setOcupat(false);
    setEditez(false);
  };

  const nume = front.catre || t("harta.strada");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <span className="mono">{front.latura + 1}</span>
        <b>{nume}</b>
        {profil ? (
          atins || profil.sursa === "manual"
            ? <span className="chip" style={{ fontSize: 10 }}>{t("loc.profil_corectat")}</span>
            : <span className={CULOARE_SIGURANTA(profil.siguranta)} style={{ fontSize: 10 }}
                    title={profil.observatii ?? ""}>
                {t("loc.profil_din_ortofoto", { s: numar((profil.siguranta ?? 0) * 100, { maximumFractionDigits: 0 }) })}
              </span>
        ) : (
          <span className="chip c-amb" style={{ fontSize: 10 }}>{t("loc.profil_lipsa")}</span>
        )}
        {potScrie && !editez && (
          <button className="btn" style={{ height: 20, fontSize: 11, marginLeft: "auto" }}
                  onClick={() => setEditez(true)}>
            {t(profil ? "loc.profil_corecteaza" : "loc.profil_scrie")}
          </button>
        )}
      </div>
      {editez ? (
        <>
          <textarea value={text} rows={Math.max(3, text.split("\n").length)}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={t("loc.profil_placeholder")}
                    style={{ fontFamily: "inherit", fontSize: 12 }} />
          <span style={{ color: "var(--mut)", fontSize: 11 }}>
            {t("loc.profil_feluri", { feluri: FELURI_BANDA.map((f) => f.replace(/_/g, " ")).join(", ") })}
          </span>
          {eroare && <span className="chip c-err" style={{ alignSelf: "flex-start" }}>{eroare}</span>}
          <div style={{ display: "flex", gap: 6 }}>
            <button className="btn btnp" style={{ height: 22, fontSize: 11 }} disabled={ocupat} onClick={scrie}>
              {t("loc.profil_salveaza")}
            </button>
            <button className="btn" style={{ height: 22, fontSize: 11 }} disabled={ocupat}
                    onClick={() => { setEditez(false); setEroare(""); setText(benziCaText(profil)); }}>
              {t("loc.renunta")}
            </button>
            {/* Întoarcerea la citire doar peste o corecție: `sursa: manual`
                vine din datele contopite, lista `manual` din chei — oricare
                din ele spune că profilul e al omului, nu al modelului. */}
            {(atins || profil?.sursa === "manual") && (
              <button className="btn" style={{ height: 22, fontSize: 11, marginLeft: "auto" }}
                      disabled={ocupat} onClick={revino} title={t("loc.profil_revino_titlu")}>
                {t("loc.profil_revino")}
              </button>
            )}
          </div>
        </>
      ) : profil ? (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {profil.benzi.map((b, i) => (
              <span key={i} className="chip c-mut" style={{ fontSize: 11 }}>
                {t(`loc.banda_${b.fel}` as Cheie)} {numar(b.latime_m, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            ))}
            <span className="mono" style={{ color: "var(--mut)", alignSelf: "center" }}>
              = {numar(profil.latime_totala_m, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m
            </span>
          </div>
          {(profil.capat_opus || profil.observatii) && (
            <span style={{ color: "var(--mut)", fontSize: 11 }}>
              {[profil.capat_opus && t("loc.profil_capat", { ce: profil.capat_opus }), profil.observatii]
                .filter(Boolean).join(" · ")}
            </span>
          )}
        </>
      ) : null}
    </div>
  );
}
